import type { ApiResult } from "./contracts";

/**
 * Клиент реквизитов организации-владельца инстанса (E15, «Настройки → Организация»).
 *
 * Доступен ТОЛЬКО администратору — сервер проверяет право `settings:manage` на каждом маршруте
 * (строка 4.3 «Настройки, справочники…»); скрытие блока в интерфейсе это лишь дублирует.
 *
 * Форма согласована с сервером по значениям (`OrgProfile` из apps/api/src/org/profile.ts).
 * До E15 реквизиты приходили из переменных окружения и менялись только перезапуском сервера.
 */

export interface OrgProfile {
  /** Полное наименование организации. */
  readonly name: string;
  readonly inn: string;
  readonly kpp: string;
  readonly ogrn: string;
  /** Почтовый (юридический) адрес одной строкой. */
  readonly address: string;
  readonly phone: string;
  readonly email: string;
}

export const EMPTY_ORG_PROFILE: OrgProfile = {
  name: "",
  inn: "",
  kpp: "",
  ogrn: "",
  address: "",
  phone: "",
  email: "",
};

/**
 * Реквизиты, без которых расчёт нельзя приложить к претензии (тот же список, что на сервере:
 * `missingOrgRequisites`). Показываются как предупреждение, но сохранению НЕ мешают: заставлять
 * администратора вписать что попало ради кнопки «Сохранить» хуже, чем видимый пробел.
 */
export const REQUIRED_ORG_FIELDS: readonly (keyof OrgProfile)[] = ["name", "inn", "address"];

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

interface ErrorBody {
  error?: string;
  message?: string;
}

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as ErrorBody | null;
  const message = body?.message;
  if (res.status === 403) {
    return {
      ok: false,
      reason: "forbidden",
      message: message ?? "Реквизиты организации меняет только администратор.",
    };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

export async function fetchOrgProfile(): Promise<ApiResult<OrgProfile>> {
  let res: Response;
  try {
    res = await fetch("/api/settings/org", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { org?: OrgProfile } | null;
  if (!body?.org) return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  return { ok: true, value: body.org };
}

/** PUT: форма присылает реквизиты целиком — частичного сохранения сервер не принимает. */
export async function saveOrgProfile(input: OrgProfile): Promise<ApiResult<OrgProfile>> {
  let res: Response;
  try {
    res = await fetch("/api/settings/org", {
      method: "PUT",
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { org?: OrgProfile } | null;
  if (!body?.org) return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  return { ok: true, value: body.org };
}
